import { type JSX } from 'react'
import { Flag, CheckCircle, XCircle, AlertTriangle, SkipForward } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import type { EvaluationIssue, IssueVerification } from '@/types/api'

interface IssueCardProps {
  issue: EvaluationIssue
  verification?: IssueVerification
  onReportError: (issueId: string) => void
  disabled?: boolean
}

const severityColors: Record<string, string> = {
  high: 'border-red-200 bg-red-50',
  medium: 'border-yellow-200 bg-yellow-50',
  low: 'border-blue-200 bg-blue-50',
}

const severityBadges: Record<string, string> = {
  high: 'bg-red-100 text-red-800',
  medium: 'bg-yellow-100 text-yellow-800',
  low: 'bg-blue-100 text-blue-800',
}

const verificationIcons: Record<string, JSX.Element> = {
  resolved: <CheckCircle className="h-4 w-4 text-green-600" />,
  unresolved: <XCircle className="h-4 w-4 text-red-600" />,
  manual_required: <AlertTriangle className="h-4 w-4 text-orange-600" />,
  skipped: <SkipForward className="h-4 w-4 text-gray-400" />,
}

const verificationLabels: Record<string, string> = {
  resolved: 'Resolved',
  unresolved: 'Unresolved',
  manual_required: 'Manual action needed',
  skipped: 'Skipped',
}

export function IssueCard({ issue, verification, onReportError, disabled }: IssueCardProps) {
  const hasReported = !!issue.user_feedback

  return (
    <div className={cn(
      'border rounded-lg p-4',
      severityColors[issue.severity] || 'border-gray-200 bg-gray-50',
      hasReported && 'opacity-60'
    )}>
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <span className={cn('px-2 py-0.5 rounded text-xs font-medium', severityBadges[issue.severity] || 'bg-gray-100 text-gray-800')}>
            {issue.severity.toUpperCase()}
          </span>
          <span className="text-xs text-gray-500">{issue.category}</span>
          {issue.subcategory && (
            <span className="text-xs text-gray-400">/ {issue.subcategory.replace(/_/g, ' ')}</span>
          )}
        </div>

        {/* Verification status */}
        {verification && (
          <div className="flex items-center gap-1 text-xs">
            {verificationIcons[verification.status]}
            <span>{verificationLabels[verification.status]}</span>
          </div>
        )}
      </div>

      {/* Title */}
      <h4 className="font-medium text-gray-900 mb-1">{issue.title}</h4>

      {/* Description */}
      {issue.description && issue.description !== issue.title && (
        <p className="text-sm text-gray-600 mb-2">{issue.description}</p>
      )}

      {/* Evidence */}
      {issue.evidence && (
        <p className="text-sm text-gray-500 mb-3 italic">
          <span className="font-medium not-italic">Evidence:</span> "{issue.evidence}"
        </p>
      )}

      {/* Suggestion */}
      {issue.suggestion && (
        <p className="text-sm text-green-700 mb-3">
          <span className="font-medium">Suggestion:</span> {issue.suggestion}
        </p>
      )}

      {/* Feedback status */}
      {issue.feedback_status && (
        <div className="mb-3 text-xs">
          {issue.feedback_status === 'pending' && (
            <span className="text-yellow-600 bg-yellow-50 px-2 py-1 rounded">
              Feedback pending admin review
            </span>
          )}
          {issue.feedback_status === 'approved' && (
            <span className="text-green-600 bg-green-50 px-2 py-1 rounded">
              Feedback approved - will be skipped in future audits
            </span>
          )}
          {issue.feedback_status === 'rejected' && (
            <span className="text-red-600 bg-red-50 px-2 py-1 rounded">
              Feedback rejected by admin
            </span>
          )}
          {(issue.feedback_status === 'rejected' || issue.feedback_status === 'approved') &&
            issue.feedback_admin_comment && (
              <p className="mt-2 text-xs text-gray-600 border-l-2 border-gray-300 pl-2 italic">
                <span className="font-medium not-italic">Reviewer's reason: </span>
                {issue.feedback_admin_comment}
              </p>
            )}
        </div>
      )}

      {/* Report error button - only show if no verification (i.e., before enhancement) */}
      {!verification && (
        <div className="flex items-center justify-between pt-2 border-t border-gray-200">
          <span className="text-xs text-gray-500">Disagree with this finding?</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onReportError(issue.issue_id)}
            disabled={disabled || hasReported}
            className="h-7 text-xs"
          >
            <Flag className="h-3 w-3 mr-1" />
            {hasReported ? 'Error Reported' : 'Report Error'}
          </Button>
        </div>
      )}
    </div>
  )
}
